import { useState } from 'react'
import Link from 'next/link'
import { Menu, X, ChevronDown } from 'lucide-react'
import { cn } from '@/atoms/lib/utils'
import { navigationConfig, ctaButtons } from '@/data/inc/navigation'

export const MobileNav = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [openDropdowns, setOpenDropdowns] = useState<string[]>([])

  const toggleDropdown = (label: string) => {
    setOpenDropdowns(prev =>
      prev.includes(label)
        ? prev.filter(item => item !== label)
        : [...prev, label]
    )
  }

  const closeMenu = () => {
    setIsOpen(false)
    setOpenDropdowns([])
  }

  return (
    <nav className="md:hidden bg-white sticky top-0 z-50">
      <div className="px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Logo Placeholder */}
          <Link href="/" onClick={closeMenu}>
            <img alt="" src="/assets/inc/nav/logo.svg" className="h-8" />
          </Link>

          {/* Hamburger Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 hover:bg-white/10 rounded-md transition-colors"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <div
        className={cn(
          'fixed inset-0 top-[60px] bg-blue-web-90 text-white transform transition-transform duration-300 ease-in-out overflow-y-auto',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="py-6 px-3 space-y-2">
          {/* Navigation Items */}
          {navigationConfig.map(item => (
            <div key={item.label} className="border-b border-white/10 pb-2">
              {item.hasDropdown ? (
                <>
                  {/* Dropdown Trigger */}
                  <button
                    onClick={() => toggleDropdown(item.label)}
                    className="w-full flex items-center justify-between py-3 px-4 text-base font-medium hover:bg-white/10 rounded-md transition-colors"
                  >
                    <span>{item.label}</span>
                    <ChevronDown
                      className={cn(
                        'w-5 h-5 transition-transform duration-200',
                        openDropdowns.includes(item.label) && 'rotate-180'
                      )}
                    />
                  </button>

                  {/* Dropdown Content */}
                  <div
                    className={cn(
                      'overflow-hidden transition-all duration-300',
                      openDropdowns.includes(item.label)
                        ? 'max-h-[2000px] opacity-100'
                        : 'max-h-0 opacity-0'
                    )}
                  >
                    {/* Tagline */}
                    {item.tagline && (
                      <p className="text-xs text-white/70 px-4 py-2">
                        {item.tagline}
                      </p>
                    )}

                    <div className="px-3 py-2 space-y-3">
                      {item.cards?.map(card => (
                        <div
                          key={card.title}
                          className="bg-white/5 rounded-lg p-4"
                        >
                          <Link
                            href={card.href}
                            onClick={closeMenu}
                            className="block"
                          >
                            <h4 className="font-medium text-sm text-white mb-1">
                              {card.title}
                            </h4>
                            {card.description && (
                              <p className="text-xs text-white/60 mb-2">
                                {card.description}
                              </p>
                            )}
                          </Link>

                          {/* Sub-items */}
                          {card.subItems && (
                            <ul className="space-y-2 mt-3 pt-3 border-t border-white/10">
                              {card.subItems.map(subItem => (
                                <li key={subItem.label}>
                                  {subItem.href ? (
                                    <Link
                                      href={subItem.href ?? 'hello'}
                                      onClick={closeMenu}
                                      className="block text-xs"
                                    >
                                      <div className="text-white font-medium">
                                        {subItem.label}
                                      </div>
                                      {subItem.description && (
                                        <div className="text-white/50 mt-0.5">
                                          {subItem.description}
                                        </div>
                                      )}
                                    </Link>
                                  ) : (
                                    <div className="block text-xs">
                                      <div className="text-white font-medium">
                                        {subItem.label}
                                      </div>
                                      {subItem.description && (
                                        <div className="text-white/50 mt-0.5">
                                          {subItem.description}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <Link
                  href={item.href || '#'}
                  onClick={closeMenu}
                  className="block py-3 px-4 text-base font-medium hover:bg-white/10 rounded-md transition-colors"
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}

          {/* CTA Buttons */}
          <div className="pt-6 space-y-3">
            {ctaButtons.map(button => (
              <Link
                key={button.label}
                href={button.href}
                onClick={closeMenu}
                className={cn(
                  'block text-center w-full',
                  button.variant === 'secondary' && 'new-btn-secondary',
                  button.variant === 'tertiary' && 'new-btn-tertiary'
                )}
              >
                {button.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[-1]"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}
    </nav>
  )
}
